import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Flag as FlagIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import api from '../api';

function ChatManager() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [channelTypeFilter, setChannelTypeFilter] = useState('');
  const [reportedOnly, setReportedOnly] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editMessageText, setEditMessageText] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, [page, rowsPerPage, searchTerm, channelTypeFilter, reportedOnly]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: rowsPerPage.toString(),
        offset: (page * rowsPerPage).toString(),
        reportedOnly: reportedOnly.toString(),
        minReports: '1'
      });

      if (searchTerm) params.append('search', searchTerm);
      if (channelTypeFilter) params.append('channelType', channelTypeFilter);

      const response = await api.get(`/chat/messages?${params}`);
      // Handle both direct JSON and wrapped GenericResponseModel format
      let data;
      if (typeof response.data === 'string') {
        data = JSON.parse(response.data);
      } else if (response.data.data) {
        data = JSON.parse(response.data.data);
      } else {
        data = response.data;
      }
      setMessages(data.messages || []);
      setTotalCount(data.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching chat messages:', err);
      setError('Failed to load chat messages');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (message) => {
    setSelectedMessage(message);
    setEditMessageText(message.message);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/chat/messages/${selectedMessage.id}`, {
        message: editMessageText
      });
      setEditDialogOpen(false);
      fetchMessages();
    } catch (err) {
      console.error('Error editing message:', err);
      setError('Failed to edit message');
    }
  };

  const handleDelete = (message) => {
    setSelectedMessage(message);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await api.put(`/chat/messages/${selectedMessage.id}`, {
        deleted: true
      });
      setDeleteDialogOpen(false);
      fetchMessages();
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('Failed to delete message');
    }
  };

  const handleRestore = async (message) => {
    try {
      await api.put(`/chat/messages/${message.id}`, {
        deleted: false
      });
      fetchMessages();
    } catch (err) {
      console.error('Error restoring message:', err);
      setError('Failed to restore message');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getChannelDisplayName = (channelType, channelId) => {
    switch (channelType) {
      case 'global':
        return 'Global';
      case 'room':
        return `Room: ${channelId}`;
      case 'archipelago':
        return `Archipelago: ${channelId}`;
      case 'friends':
        return `Friends: ${channelId}`;
      default:
        return `${channelType}: ${channelId}`;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Chat Message Management
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Channel Type</InputLabel>
            <Select
              value={channelTypeFilter}
              label="Channel Type"
              onChange={(e) => {
                setChannelTypeFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="global">Global</MenuItem>
              <MenuItem value="room">Room</MenuItem>
              <MenuItem value="archipelago">Archipelago</MenuItem>
              <MenuItem value="friends">Friends</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant={reportedOnly ? 'contained' : 'outlined'}
            color="error"
            startIcon={<FlagIcon />}
            onClick={() => {
              setReportedOnly(!reportedOnly);
              setPage(0);
            }}
          >
            Reported Only ({reportedOnly ? 'ON' : 'OFF'})
          </Button>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchMessages}
          >
            Refresh
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Messages Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Channel</TableCell>
              <TableCell>Sender</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Reports</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : messages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No messages found
                </TableCell>
              </TableRow>
            ) : (
              messages.map((message) => (
                <TableRow key={message.id} hover>
                  <TableCell>
                    <Chip
                      label={getChannelDisplayName(message.channelType, message.channelId)}
                      size="small"
                      color={
                        message.channelType === 'global' ? 'primary' :
                        message.channelType === 'room' ? 'secondary' :
                        message.channelType === 'archipelago' ? 'info' : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {message.senderName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {message.senderId.substring(0, 8)}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {message.message}
                    </Typography>
                    {message.editedAt && (
                      <Typography variant="caption" color="text.secondary">
                        (edited)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatTimestamp(message.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {message.reportedCount > 0 ? (
                      <Chip
                        label={message.reportedCount}
                        size="small"
                        color="error"
                        icon={<FlagIcon />}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        0
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {message.deletedAt ? (
                      <Chip label="Deleted" size="small" color="error" />
                    ) : (
                      <Chip label="Active" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="Edit message">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(message)}
                          disabled={!!message.deletedAt}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {message.deletedAt ? (
                        <Tooltip title="Restore message">
                          <IconButton
                            size="small"
                            onClick={() => handleRestore(message)}
                            color="success"
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Delete message">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(message)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
      />

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Message</DialogTitle>
        <DialogContent>
          <DialogContentText gutterBottom>
            Channel: {selectedMessage && getChannelDisplayName(selectedMessage.channelType, selectedMessage.channelId)}
            <br />
            Sender: {selectedMessage?.senderName} ({selectedMessage?.senderId?.substring(0, 8)}...)
            <br />
            Created: {selectedMessage && formatTimestamp(selectedMessage.createdAt)}
            {selectedMessage?.reportedCount > 0 && (
              <>
                <br />
                <Chip
                  label={`${selectedMessage.reportedCount} Report(s)`}
                  size="small"
                  color="error"
                  icon={<FlagIcon />}
                  sx={{ mt: 1 }}
                />
              </>
            )}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={4}
            value={editMessageText}
            onChange={(e) => setEditMessageText(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this message? This will soft-delete it (hide from users but keep in database).
            <br /><br />
            <strong>Message:</strong> {selectedMessage?.message}
            <br />
            <strong>Sender:</strong> {selectedMessage?.senderName}
            <br />
            {selectedMessage?.reportedCount > 0 && (
              <>
                <strong>Reports:</strong> {selectedMessage.reportedCount}
                <br />
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ChatManager;

